import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  AcademicCapIcon
} from '@heroicons/react/24/outline';
import { parameterAPI } from '../services/api';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import toast from 'react-hot-toast';

const AdminFonctions = () => {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    libelle: '',
    description: '',
    actif: true
  });

  // Récupérer les fonctions
  const { data: fonctionsData, isLoading } = useQuery(
    'fonctions',
    () => parameterAPI.getFonctions()
  );

  const fonctions = fonctionsData?.data?.data || fonctionsData?.data || [];

  // Mutation pour créer/modifier
  const saveMutation = useMutation(
    (data) => editingItem 
      ? parameterAPI.updateFonction(editingItem.id, data)
      : parameterAPI.createFonction(data),
    {
      onSuccess: () => {
        toast.success(editingItem ? 'Fonction modifiée avec succès' : 'Fonction créée avec succès');
        queryClient.invalidateQueries('fonctions');
        handleCloseModal();
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Erreur lors de la sauvegarde');
      }
    }
  );

  // Mutation pour supprimer
  const deleteMutation = useMutation(
    (id) => parameterAPI.deleteFonction(id),
    {
      onSuccess: () => {
        toast.success('Fonction supprimée avec succès');
        queryClient.invalidateQueries('fonctions');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Erreur lors de la suppression');
      }
    }
  );

  const handleOpenModal = (item = null) => {
    setEditingItem(item);
    setFormData(item ? {
      libelle: item.libelle,
      description: item.description || '',
      actif: item.actif
    } : {
      libelle: '',
      description: '',
      actif: true
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setFormData({ libelle: '', description: '', actif: true });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.libelle.trim()) {
      toast.error('Le libellé est requis');
      return;
    }
    saveMutation.mutate(formData);
  };

  const handleDelete = (item) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer "${item.libelle}" ?`)) {
      deleteMutation.mutate(item.id);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="bg-white rounded-xl shadow-card p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <AcademicCapIcon className="h-8 w-8 text-purple-600" />
            <div>
              <h1 className="text-2xl font-bold text-primary-900">
                Fonctions Professionnelles
              </h1>
              <p className="text-gray-600">
                Gérez les fonctions et métiers disponibles dans le système
              </p>
            </div>
          </div>
          <button
            onClick={() => handleOpenModal()}
            className="btn-primary flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Ajouter
          </button>
        </div>
      </div>

      {/* Liste des fonctions */}
      <div className="bg-white rounded-xl shadow-card overflow-hidden">
        {fonctions.length === 0 ? (
          <div className="text-center py-12">
            <AcademicCapIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune fonction</h3>
            <p className="mt-1 text-sm text-gray-500">
              Commencez par ajouter des fonctions professionnelles.
            </p>
            <div className="mt-6">
              <button
                onClick={() => handleOpenModal()}
                className="btn-primary"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Ajouter une fonction
              </button>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Libellé
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {fonctions.map((fonction) => (
                  <tr key={fonction.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {fonction.libelle}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500">
                        {fonction.description || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        fonction.actif 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {fonction.actif ? 'Actif' : 'Inactif'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleOpenModal(fonction)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Modifier"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(fonction)}
                          className="text-red-600 hover:text-red-900"
                          title="Supprimer"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal d'ajout/modification */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseModal} />

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-purple-100 sm:mx-0 sm:h-10 sm:w-10">
                      <AcademicCapIcon className="h-6 w-6 text-purple-600" />
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        {editingItem ? 'Modifier la fonction' : 'Ajouter une fonction'}
                      </h3>
                      <div className="mt-4 space-y-4">
                        <div>
                          <label className="form-label">Libellé *</label>
                          <input
                            type="text"
                            value={formData.libelle}
                            onChange={(e) => setFormData({ ...formData, libelle: e.target.value })}
                            className="form-input"
                            placeholder="Ex: Directeur, Comptable, Secrétaire..."
                            required
                          />
                        </div>
                        <div>
                          <label className="form-label">Description</label>
                          <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            className="form-input"
                            rows="3"
                            placeholder="Description de la fonction..."
                          />
                        </div>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="actif"
                            checked={formData.actif}
                            onChange={(e) => setFormData({ ...formData, actif: e.target.checked })}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          />
                          <label htmlFor="actif" className="ml-2 block text-sm text-gray-900">
                            Actif
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={saveMutation.isLoading}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  >
                    {saveMutation.isLoading ? (
                      <LoadingSpinner size="small" className="mr-2" />
                    ) : null}
                    {editingItem ? 'Modifier' : 'Ajouter'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminFonctions;
